/**
 * Mit jelent az, amit a pultnál beütöttek vagy beolvastak (#168).
 *
 * MIÉRT KLIENSOLDALON, ÉS MIÉRT NINCS HOZZÁ BACKEND. Karlo terve a QR-kód
 * feloldásához admin oldali végpontot javasolt, mert ma csak a publikus és az
 * ajtós út bontja szét a `{eventId}-{ticketNumber}` alakot. Lemérve viszont
 * nem kell: a #164 óta a Bejelentkeztetés képernyő az esemény MINDEN jegyét
 * betölti (`fetchAllTickets`), tehát a feloldáshoz szükséges jegy már a
 * memóriában van. Egy új végpont ugyanazt az adatot kérné le még egyszer, egy
 * hálózati körrel a pultnál — ott, ahol sorban állnak.
 *
 * MIÉRT TISZTA FÜGGVÉNY. A pult-modális viselkedése ezen a döntésen áll, és a
 * döntés négyféle lehet. Egy képernyőbe ágyazva mind a négy ág csak felmountolt
 * komponensen, hamis szerverrel és szimulált billentyűzettel lenne mérhető; így
 * négy sor.
 *
 * AMIT A SORREND VÉD. Az USB-s QR olvasó BILLENTYŰZETKÉNT gépel a keresőmezőbe,
 * majd Entert üt. Ugyanabba a mezőbe gépel a kezelő is, amikor nevet keres.
 * A kettőt meg kell tudni különböztetni: egy „Kovács" utáni Enterre nem
 * ugorhat fel „Nem található" ablak. Ezért van a {@link NOT_A_CODE} ág — az
 * Enter ilyenkor sima keresés marad.
 */

/** A `{eventId}-{ticketNumber}` alak ELSŐ fele: egy cuid. */
const CUID_PREFIX = /^([a-z0-9]{20,})-(.+)$/;

export const NOT_A_CODE = 'not-a-code' as const;

export interface ScannableTicket {
  ticketNumber: string;
}

export type ScanResult<T extends ScannableTicket> =
  /** Megvan a jegy ezen az eseményen. */
  | { kind: 'ticket'; ticket: T }
  /** Jegykódnak látszik, de MÁSIK esemény jegye. */
  | { kind: 'other-event' }
  /** Jegykódnak látszik, de ezen a listán nincs ilyen. */
  | { kind: 'not-found' }
  /** Nem jegykód — a kezelő nevet vagy céget gépelt. Marad a sima keresés. */
  | { kind: typeof NOT_A_CODE };

/**
 * A beolvasott / beütött szöveg feloldása a már betöltött jegylistán.
 *
 * @param raw       amit a keresőmezőbe gépeltek
 * @param tickets   az AKTUÁLIS esemény betöltött jegyei
 * @param eventId   az aktuális esemény azonosítója
 */
export function resolveScan<T extends ScannableTicket>(
  raw: string,
  tickets: readonly T[],
  eventId: string | undefined
): ScanResult<T> {
  const value = raw.trim();
  if (!value) return { kind: NOT_A_CODE };

  // 1. Pontos jegyszám. A kezelő be is gépelheti a papírról, és a kis/nagy
  //    betűt nem őrizzük meg rajta — a jegyszám azonosító, nem szöveg.
  const byNumber = tickets.find((t) => t.ticketNumber.toLowerCase() === value.toLowerCase());
  if (byNumber) return { kind: 'ticket', ticket: byNumber };

  // 2. A JELEN esemény QR-je. Ez az ág nem függ az azonosító alakjától: ha a
  //    beolvasott szöveg a kiválasztott esemény azonosítójával kezdődik, akkor
  //    ez a mi kódunk, bármilyen azonosítóformátumra váltana is a rendszer.
  if (eventId && value.startsWith(`${eventId}-`)) {
    const ticketNumber = value.slice(eventId.length + 1);
    const match = tickets.find((t) => t.ticketNumber.toLowerCase() === ticketNumber.toLowerCase());
    return match ? { kind: 'ticket', ticket: match } : { kind: 'not-found' };
  }

  // 3. IDEGEN azonosítóval kezdődő kód. Itt már csak az ALAK dönthet, mert a
  //    másik esemény azonosítóját nem ismerjük. A hosszú, egybefüggő előtag az,
  //    ami megkülönbözteti egy keresőszótól: a jegyszámok („CG26-0087") és a
  //    kötőjeles cégnevek („Delhaize-Serbia") rövidebb előtaggal állnak.
  const parsed = CUID_PREFIX.exec(value);
  if (!parsed) return { kind: NOT_A_CODE };

  const ticketNumber = parsed[2];
  // Csak akkor állítjuk, hogy MÁSIK esemény jegye, ha a farok tényleg
  // jegyszámnak látszik — különben a kezelő rossz tanácsot kap („váltson
  // eseményt") egy elgépelt keresőszóra.
  const looksLikeTicket = tickets.some(
    (t) => t.ticketNumber.toLowerCase() === ticketNumber.toLowerCase()
  );
  return looksLikeTicket ? { kind: 'other-event' } : { kind: 'not-found' };
}

/**
 * Kód-e a bemenet — A LISTA ISMERETE NÉLKÜL (#168)?
 *
 * MIÉRT KELL KÜLÖN A {@link resolveScan} MELLÉ. A feloldás a betöltött listára
 * támaszkodik, tehát amíg a lista nincs meg, MINDEN válasza gyanús: az üres
 * listán a jelen esemény QR-je „nincs ilyen jegy"-nek látszik, eseményváltás
 * közben pedig az előző esemény sorai még ott vannak. A pult képernyőjének
 * ezért tudnia kell, hogy ÉRDEMES-E megvárnia a listát ezzel a bemenettel —
 * és ezt a kérdést csak az alak döntheti el.
 *
 * MIÉRT NEM AZ ÖSSZESET VÁRJUK MEG. A kezelő ugyanabba a mezőbe gépel nevet,
 * mint amibe az olvasó a kódot küldi. Ha a képernyő minden Enterre várakozó
 * ablakot nyitna, amíg a lista töltődik, akkor egy „Kovács" is felugró ablakot
 * kapna — pontosan az, amit a {@link NOT_A_CODE} ág elkerül. Ezért itt csak a
 * KÉTSÉGTELEN kódra mondunk igent:
 *
 *   - a jelen esemény azonosítójával kezdődő szöveg: ezt a QR-t mi állítottuk
 *     elő, listától függetlenül felismerhető;
 *   - a hosszú, cuid alakú előtaggal álló szöveg: idegen esemény kódja.
 *
 * AMI SZÁNDÉKOSAN KIMARAD: a papírról BEGÉPELT csupasz jegyszám („CG26-0087").
 * Azt betöltött lista nélkül semmi nem különbözteti meg egy keresőszótól, és a
 * két tévedés közül a rosszabbik a felugró ablak egy névre. Ilyenkor a mai
 * viselkedés marad — sima keresés —, ami legfeljebb üres táblát mutat, nem ad
 * hamis választ.
 */
export function looksLikeCode(raw: string, eventId: string | undefined): boolean {
  const value = raw.trim();
  if (!value) return false;
  if (eventId && value.startsWith(`${eventId}-`)) return true;
  // `test`, nem `exec`: a minta `g` jelző nélkül áll, tehát nincs `lastIndex`,
  // amit két hívás között magával vinne.
  return CUID_PREFIX.test(value);
}

/**
 * Hol kezdődik a jelen esemény jegykódja egy begépelt szövegben, vagy -1.
 *
 * MIÉRT (#168, 2026-09-18). A nyitott beolvasás-ablak elsősorban a gépi
 * TEMPÓBÓL ismeri fel az olvasót. Egy lassabban küldő olvasó (egyes vezeték
 * nélküli típusok, beállított karakterköz) ezen átcsúszna, és a nyitott
 * ablakban látható vendég címkéje jönne ki. Ez a második jel a TARTALOM: a
 * jelen esemény azonosítója és egy kötőjel olyan szöveg, amit a pultos egy
 * névbe vagy cégnévbe nem gépel.
 *
 * Az UTOLSÓ előfordulást keresi, mert a sorozat elején a pultos saját,
 * korábbi leütései is állhatnak — azok nem részei a kódnak.
 */
export function codeStartIn(typed: string, eventId: string | undefined): number {
  if (!eventId) return -1;
  return typed.lastIndexOf(`${eventId}-`);
}
